import React from 'react';
import Konva from 'konva';
import { Group, Circle, Text as KonvaText } from 'react-konva';
import { useDiagramStore } from '../../stores/diagramStore';

interface ConnectorOverlayProps {
  edgeId: string;
  midpoint: { x: number; y: number } | null;
  canShowInsert: boolean;
  onHoverChange?: (hovered: boolean) => void;
}

const ConnectorOverlayBody: React.FC<ConnectorOverlayProps & { midpoint: { x: number; y: number } }> = ({
  edgeId,
  midpoint,
  canShowInsert,
  onHoverChange,
}) => {
  // If a connector label exists, offset the "+" button away from the label so it doesn't block label selection/edit.
  const edge = useDiagramStore(
    React.useCallback((s) => s.edges.find((e) => e.id === edgeId), [edgeId])
  );
  const label = (edge?.attrs as any)?.label as string | undefined;
  const suppressed = (edge?.attrs as any)?.__captionHidden === true;
  const hasLabel = !suppressed && typeof label === 'string' && label.trim().length > 0;
  const overlayOffset = React.useMemo(() => {
    if (!hasLabel) return { x: 0, y: 0 };
    const fontPx = 14;
    const lineH = 1.2;
    const lineCount = Math.max(1, String(label).split('\n').length);
    const connectorWidth = typeof (edge?.attrs as any)?.width === 'number' ? ((edge?.attrs as any).width as number) : 2;
    const padY = Math.max(4, Math.ceil(connectorWidth) + 6);
    const labelHeight = Math.ceil(lineCount * fontPx * lineH) + padY;
    // Keep the button close to the midpoint, but clearly outside the label box.
    const dy = -Math.min(60, Math.max(24, Math.ceil(labelHeight / 2) + 16));
    return { x: 0, y: dy };
  }, [edge?.attrs, hasLabel, label]);

  const handleOpenQuickCreate = (e: any) => {
    e.cancelBubble = true;
    e.evt?.stopPropagation?.();
    try {
      if (!midpoint) return;
      const stage = e.target?.getStage?.();
      const container = stage?.container?.();
      if (!stage || !container) return;
      const rect = container.getBoundingClientRect();
      const scale = stage.scaleX?.() || 1;
      const pos = stage.position?.() || { x: 0, y: 0 };
      const screenX = rect.left + pos.x + midpoint.x * scale;
      const screenY = rect.top + pos.y + midpoint.y * scale;
      const detail = {
        edgeId,
        midpointX: midpoint.x,
        midpointY: midpoint.y,
        screenX,
        screenY,
      } as const;
      window.dispatchEvent(new CustomEvent('canvas:insert-between:open', { detail }));
    } catch {}
  };

  if (!canShowInsert || !midpoint) return null;

  return (
    <Group
      x={midpoint.x + overlayOffset.x}
      y={midpoint.y + overlayOffset.y}
      onMouseEnter={() => {
        onHoverChange?.(true);
        document.body.style.cursor = 'pointer';
      }}
      onMouseLeave={() => {
        onHoverChange?.(false);
        document.body.style.cursor = 'default';
      }}
    >
      <Circle
        radius={12}
        fill="#3b82f6"
        stroke="#ffffff"
        strokeWidth={2}
        onClick={handleOpenQuickCreate}
        onTap={handleOpenQuickCreate}
        onMouseEnter={(e) => {
          const shape = e.target as Konva.Circle;
          shape.fill('#2563eb');
          onHoverChange?.(true);
          document.body.style.cursor = 'pointer';
        }}
        onMouseLeave={(e) => {
          const shape = e.target as Konva.Circle;
          shape.fill('#3b82f6');
          onHoverChange?.(false);
          document.body.style.cursor = 'default';
        }}
      />
      <KonvaText
        x={0}
        y={0}
        text="+"
        fontSize={16}
        fontStyle="bold"
        fill="#ffffff"
        align="center"
        verticalAlign="middle"
        width={24}
        height={24}
        offsetX={12}
        offsetY={12}
        listening={false}
      />
    </Group>
  );
};

export const ConnectorOverlay: React.FC<ConnectorOverlayProps> = (props) => {
  if (!props.canShowInsert || !props.midpoint) return null;
  return <ConnectorOverlayBody {...props} midpoint={props.midpoint} />;
};
